import { Navigate, useLocation } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { hasAnyRole } from './accessPolicies';
import type { Role } from '../types/auth';

interface Props {
  children: ReactNode;
  /** If set, user must have at least one of these roles. */
  requireAnyRole?: readonly Role[];
}

export function ProtectedRoute({ children, requireAnyRole }: Props): JSX.Element {
  const { isAuthenticated, isBootstrapping, user } = useAuth();
  const location = useLocation();

  if (isBootstrapping) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  if (user.mustCompleteProfile && location.pathname !== '/profile') {
    return <Navigate to="/profile" replace />;
  }

  if (requireAnyRole && requireAnyRole.length > 0 && !hasAnyRole(user.roles, requireAnyRole)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
